import type { ClinicSession } from "../../shared/api/clinicSessions";
import type { WorkShift } from "../../shared/api/workShifts";

/**
 * #19954 — khoảng giờ của Buổi khám không có bác sĩ nào trực, theo từng thứ trong tuần.
 *
 * <p>Từ #19954 lưới đặt lịch lấy từ Buổi khám, không từ Phân ca: khung không ai trực VẪN đặt được,
 * lịch rơi vào trạng thái không bác sĩ và lễ tân phải chỉ định sau. Màn Lịch làm việc báo trước
 * những khoảng đó để phòng khám xếp ca. Chỉ là cảnh báo, không chặn gì.
 *
 * <p>Chỉ xét ca lặp theo thứ (ca theo ngày cụ thể là ngoại lệ một ngày, không nói gì về tuần) và
 * chỉ ca CÓ tên bác sĩ — ca để trống bác sĩ không cho biết ai khám. Ca khai theo buổi quy ra giờ
 * của buổi đó.
 */
export interface UncoveredRange {
  label: string;
  start: string;
  end: string;
}

function toMin(hhmm: string): number {
  const [h, m] = hhmm.slice(0, 5).split(":").map(Number);
  return h * 60 + m;
}

function fmt(min: number): string {
  return `${String(Math.floor(min / 60)).padStart(2, "0")}:${String(min % 60).padStart(2, "0")}`;
}

/** Khoảng giờ của các buổi đang bật mà không ca có bác sĩ nào phủ, ngày thứ `weekday` (ISO). */
export function uncoveredOnWeekday(
  sessions: readonly ClinicSession[],
  shifts: readonly WorkShift[],
  weekday: number,
): UncoveredRange[] {
  const active = sessions.filter((s) => s.active);
  const byCode = new Map(active.map((s) => [s.code, s]));
  const covered: [number, number][] = [];
  for (const w of shifts) {
    if (
      !w.active ||
      !w.doctorProfileId ||
      w.specificDate ||
      w.weekday !== weekday
    )
      continue;
    const start =
      w.startTime ??
      (w.sessionCode ? byCode.get(w.sessionCode)?.startTime : null);
    const end =
      w.endTime ?? (w.sessionCode ? byCode.get(w.sessionCode)?.endTime : null);
    if (start && end) covered.push([toMin(start), toMin(end)]);
  }
  const out: UncoveredRange[] = [];
  for (const s of active
    .slice()
    .sort((a, b) => a.startTime.localeCompare(b.startTime))) {
    let cursor = toMin(s.startTime);
    const end = toMin(s.endTime);
    // Trừ dần các ca phủ lên buổi, theo giờ bắt đầu.
    for (const [cs, ce] of covered
      .filter(([cs, ce]) => cs < end && ce > cursor)
      .sort((a, b) => a[0] - b[0])) {
      if (cs > cursor)
        out.push({
          label: s.label,
          start: fmt(cursor),
          end: fmt(Math.min(cs, end)),
        });
      cursor = Math.max(cursor, ce);
      if (cursor >= end) break;
    }
    if (cursor < end)
      out.push({ label: s.label, start: fmt(cursor), end: fmt(end) });
  }
  return out;
}

/**
 * Gộp các thứ có CÙNG khoảng thiếu người trực thành một dòng ("T2, T3, T4: Trưa 11:30–13:30").
 * `weekdays` là các thứ phòng khám mở cửa; thứ không có khoảng thiếu thì bỏ.
 */
export function uncoveredByWeekday(
  sessions: readonly ClinicSession[],
  shifts: readonly WorkShift[],
  weekdays: readonly number[],
): { weekdays: number[]; ranges: UncoveredRange[] }[] {
  const groups = new Map<
    string,
    { weekdays: number[]; ranges: UncoveredRange[] }
  >();
  for (const wd of weekdays) {
    const ranges = uncoveredOnWeekday(sessions, shifts, wd);
    if (ranges.length === 0) continue;
    const key = ranges.map((r) => `${r.label}|${r.start}|${r.end}`).join(";");
    const g = groups.get(key);
    if (g) g.weekdays.push(wd);
    else groups.set(key, { weekdays: [wd], ranges });
  }
  return [...groups.values()];
}
